/* Fuel price shelf on Analytics → Fuel.
 *
 * Premium / Enterprise: show diesel + petrol ceilings from fuelPrices, with a
 * "Test EPRA connection" button that hits /api/premium/fuel/refresh-prices.
 * Basic: inline FuelPricesPage (Google Places + typed Ksh/L) — they are the
 * price librarian; no EPRA cron for them.
 */

import React, { useCallback, useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db, auth } from "../firebase";
import FuelPricesPage, { currentMonthKey } from "./FuelPricesPage";
import {
  NEARBY_FUEL_STATION_TARGET,
  PRIMARY_FUEL_STATION_LIMIT,
} from "../utils/nearbyFuelStations";

const BASE = (process.env.REACT_APP_PAYMENTS_URL || "").replace(/\/$/, "");

type Caps = { diesel: number | null; petrol: number | null };

interface Props {
  fleetOrgCode: string;
  /** Premium / Enterprise → EPRA test. Basic → Places upload. */
  isZanaTier: boolean;
}

export default function FuelPriceShelfCard({ fleetOrgCode, isZanaTier }: Props) {
  const month = currentMonthKey();
  const [source, setSource] = useState<string | null>(null);
  const [caps, setCaps] = useState<Caps>({ diesel: null, petrol: null });
  const [stationCount, setStationCount] = useState(0);
  const [primaryCount, setPrimaryCount] = useState(0);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showEditor, setShowEditor] = useState(!isZanaTier);

  useEffect(() => {
    if (!fleetOrgCode) return;
    const ref = doc(db, "fuelPrices", `${fleetOrgCode}__${month}`);
    return onSnapshot(
      ref,
      (snap) => {
        const d = snap.data() || {};
        const byType = (d.regionalDefaultPerLByType || d.caps || {}) as Caps;
        setSource(d.source || null);
        setCaps({
          diesel: byType.diesel ?? d.regionalDefaultPerL ?? null,
          petrol: byType.petrol ?? null,
        });
        const list = Array.isArray(d.stations) ? d.stations : [];
        const placeIds = new Set(
          list.map((s: { placeId?: string }) => s.placeId).filter(Boolean)
        );
        const primaries = new Set(
          list
            .filter((s: { primary?: boolean; placeId?: string }) => s.primary && s.placeId)
            .map((s: { placeId?: string }) => s.placeId)
        );
        setStationCount(placeIds.size);
        setPrimaryCount(primaries.size);
        setNote(typeof d.note === "string" ? d.note : null);
      },
      () => {
        /* ignore — shelf just stays empty */
      }
    );
  }, [fleetOrgCode, month]);

  const refreshEpra = useCallback(async () => {
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      if (!BASE) {
        setErr("Payments API URL is not configured (REACT_APP_PAYMENTS_URL).");
        return;
      }
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        setErr("Sign in again to test EPRA.");
        return;
      }
      const res = await fetch(`${BASE}/api/premium/fuel/refresh-prices`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ orgCode: fleetOrgCode }),
      });
      const body = await res.json().catch(() => ({}));
      if (body.caps) {
        setCaps({
          diesel: body.caps.diesel ?? null,
          petrol: body.caps.petrol ?? null,
        });
        if (body.source) setSource(body.source);
      }
      if (res.status === 404) {
        setErr(
          "EPRA test route is not on the live payments server yet — redeploy pear-payments, then try again. Until then, add stations below so prices are real."
        );
        return;
      }
      if (!res.ok) {
        setErr(body.message || `Refresh failed (${res.status}).`);
        return;
      }
      if (!body.ok) {
        // Fallback ceilings may still have been written + admin emailed.
        setErr(body.message || "EPRA unavailable — regional fallback applied.");
        if (body.fallbackApplied) {
          setMsg(
            body.alertEmailed
              ? "False data alert emailed to the fleet admin. Regional ceilings are on file until EPRA is back."
              : "Regional fallback ceilings are on file until EPRA is back. Add your stations for real pump prices."
          );
        }
        return;
      }
      setMsg(body.message || "EPRA prices refreshed.");
    } catch (e: unknown) {
      const raw = e instanceof Error ? e.message : "Network error";
      setErr(
        /failed to fetch/i.test(raw)
          ? "Could not reach the payments server (network / CORS / undeployed route). Redeploy pear-payments if you just added the EPRA test, or add stations below as a fallback."
          : raw
      );
    } finally {
      setBusy(false);
    }
  }, [fleetOrgCode]);

  const fmt = (n: number | null) =>
    n != null && Number.isFinite(n) ? `KES ${Number(n).toFixed(0)}/L` : "—";

  return (
    <div className="card card-pad" style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div>
          <div className="card-title" style={{ marginBottom: 4 }}>
            Fuel prices · {month}
          </div>
          <div className="card-note" style={{ maxWidth: 520 }}>
            A station on your list uses the Ksh/L you set for it. Any other pump uses the EPRA
            ceiling. The driver confirms the shillings.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {isZanaTier && (
            <button
              type="button"
              onClick={() => void refreshEpra()}
              disabled={busy}
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                border: "none",
                background: busy ? "#c4b5fd" : "#7c3aed",
                color: "#fff",
                fontWeight: 700,
                fontSize: 13,
                cursor: busy ? "wait" : "pointer",
              }}
            >
              {busy ? "Talking to EPRA…" : "Refresh EPRA ceilings"}
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowEditor((v) => !v)}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: "1px solid #cbd5e1",
              background: "#fff",
              color: "#0f172a",
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            {showEditor
              ? "Hide stations"
              : isZanaTier
                ? stationCount === 0
                  ? "Ask Zana / manage stations"
                  : "Add / edit stations"
                : stationCount < NEARBY_FUEL_STATION_TARGET
                  ? "Add more stations"
                  : "Manage stations"}
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginTop: 14, fontSize: 14 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Diesel</div>
          <div style={{ fontWeight: 800, color: "#0f172a", fontSize: 18 }}>{fmt(caps.diesel)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Petrol</div>
          <div style={{ fontWeight: 800, color: "#0f172a", fontSize: 18 }}>{fmt(caps.petrol)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Source</div>
          <div style={{ fontWeight: 600, color: "#334155", textTransform: "capitalize" }}>
            {source || "none yet"}
            {stationCount > 0
              ? ` · ${stationCount} station${stationCount === 1 ? "" : "s"}`
              : ""}
            {isZanaTier && primaryCount > 0
              ? ` · ${primaryCount} primary`
              : ""}
          </div>
        </div>
      </div>

      {note && (
        <div style={{ marginTop: 10, fontSize: 12.5, color: "#64748b" }}>{note}</div>
      )}
      {msg && (
        <div style={{ marginTop: 10, fontSize: 13, color: "#16a34a", fontWeight: 600 }}>{msg}</div>
      )}
      {err && (
        <div style={{ marginTop: 10, fontSize: 13, color: "#dc2626", fontWeight: 600 }}>{err}</div>
      )}

      {showEditor && (
        <div style={{ marginTop: 16, borderTop: "1px solid #e2e8f0", paddingTop: 16 }}>
          <FuelPricesPage fleetOrgCode={fleetOrgCode} isZanaTier={isZanaTier} embedded />
        </div>
      )}
    </div>
  );
}
